// postImages.ts — normalise a post's media into ONE shape the UI can render.
//
// A post can carry media in three different ways, for historical reasons:
//   1. `images: [...]`  — the current multi-image field (preferred).
//   2. `media: "https://…"` — a legacy single uploaded image, from before `images` existed.
//   3. `media: "cityscape" | "globe"` — decorative CSS/SVG variants used by seeded demo
//      posts. These are NOT photographs: they must never open in the image viewer.
//
// Every consumer should go through `postImages()` / `isDecorativeMedia()` rather than
// re-deriving these rules, so the three shapes stay handled consistently.

import type { Post, PostImage, PostMedia } from '@/core/models/post';
import { isHttpUrl } from '@/core/lib/isHttpUrl';

/** The decorative, non-photographic media variants. */
export const DECORATIVE_MEDIA = ['cityscape', 'globe'] as const;

/** True when `media` is a decorative variant rather than a real image. */
export function isDecorativeMedia(media: PostMedia | undefined): boolean {
  return typeof media === 'string' && (DECORATIVE_MEDIA as readonly string[]).includes(media);
}

/**
 * The post's real images, in display order. Empty for text-only posts and for posts whose
 * only media is a decorative variant (render those with <PostMedia> instead).
 */
export function postImages(post: Pick<Post, 'media' | 'images'>): PostImage[] {
  const list = post.images;
  if (list && list.length > 0) {
    // Defend against a malformed API payload: drop entries without a usable url.
    return list.filter((img): img is PostImage =>
      Boolean(img && typeof img.url === 'string' && img.url),
    );
  }
  // Legacy single-image post: synthesise a one-item list so callers have one code path.
  if (isHttpUrl(post.media)) {
    return [{ url: post.media as string, alt: '', width: null, height: null }];
  }
  return [];
}

/** True when the post has at least one real (viewable) image. */
export function hasImages(post: Pick<Post, 'media' | 'images'>): boolean {
  return postImages(post).length > 0;
}

/**
 * True when the post belongs in a Media tab: real images, one of the two decorative seed
 * variants, or an uploaded video (2026-09-26). NOT `media !== undefined` — the server sends
 * `media: ""` for text posts, so that test let every text post in as an empty tile.
 */
export function hasMedia(
  post: Pick<Post, 'media' | 'images'> & Partial<Pick<Post, 'video'>>,
): boolean {
  return hasImages(post) || isDecorativeMedia(post.media) || Boolean(post.video);
}

/**
 * Intrinsic aspect ratio (width / height) when both dimensions are known, else null.
 * Callers use null to fall back to a default box rather than guessing.
 */
export function aspectOf(img: PostImage): number | null {
  if (!img.width || !img.height || img.width <= 0 || img.height <= 0) return null;
  return img.width / img.height;
}
